import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Zap } from "lucide-react";

interface Props {
  balance: number;
  isLoading?: boolean;
  onUpgrade: () => void;
  className?: string;
  variant?: "compact" | "card";
}

export function TokenUpgradeWidget({
  balance,
  isLoading = false,
  onUpgrade,
  className = "",
  variant = "compact",
}: Props) {
  const { t } = useTranslation(["common"]);

  if (variant === "card") {
    if (isLoading) {
      return (
        <div className={`bg-white rounded-xs p-2.5 space-y-2 ${className}`}>
          <div className="flex items-center gap-2">
            <Skeleton className="size-8 rounded-xs shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-2.5 w-20" />
              <Skeleton className="h-2 w-32" />
            </div>
          </div>
          <Skeleton className="h-7 w-full rounded-xxs" />
        </div>
      );
    }

    return (
      <div className={`bg-white rounded-xs p-2.5 space-y-2.5 ${className}`}>
        <div className="flex items-center gap-2">
          <img
            src="/icons/interface-icons/shared/icon-3d-gold-coins.png"
            alt={t("common:chrome.credits")}
            draggable={false}
            className="size-8 object-contain shrink-0"
          />
          <div className="min-w-0">
            <p className="text-[11px] text-gray-400 font-medium tracking-wide">
              {t("company:subscription.ai_tokens")}
            </p>
            <p className="text-sm font-bold text-black tabular-nums">
              {balance.toLocaleString()}
              <span className="text-xs font-normal text-gray-400 ms-1">
                {t("common:chrome.remaining")}
              </span>
            </p>
          </div>
        </div>
        <button
          onClick={onUpgrade}
          className="w-full flex items-center justify-center gap-1.5 bg-gradient-subscription-active text-white text-xs font-semibold py-1.5 rounded-xxs hover:opacity-90 transition-opacity"
        >
          <Zap size={11} />
          {t("common:chrome.upgrade_tokens")}
        </button>
      </div>
    );
  }

  // compact — header style
  if (isLoading) {
    return (
      <div
        className={`flex items-center bg-white rounded-xs h-9 border border-black/10 shadow-sm overflow-hidden ${className}`}
      >
        <div className="flex items-center gap-1.5 ps-1.5 pe-2">
          <Skeleton className="size-6 rounded-xxs" />
          <Skeleton className="h-3 w-8" />
        </div>
        <Skeleton className="h-full w-16 rounded-none" />
      </div>
    );
  }

  return (
    <div
      className={`flex items-center bg-white rounded-xs h-9 border border-black/10 shadow-sm overflow-hidden ${className}`}
    >
      <div className="flex items-center gap-1.5 ps-1.5 pe-2 shrink-0">
        <img
          src="/icons/interface-icons/shared/icon-3d-gold-coins.png"
          alt={t("common:chrome.credits")}
          draggable={false}
          className="size-6 object-contain shrink-0"
        />
        <span className="text-[12px] font-semibold tabular-nums shrink-0">
          {balance.toLocaleString()}
        </span>
      </div>
      <Button
        size="xs"
        className="bg-gradient-subscription-active hover:opacity-80 transition-opacity h-full rounded-none shrink-0"
        onClick={onUpgrade}
      >
        {t("common:header.upgrade")}
      </Button>
    </div>
  );
}
